// (c) chaehongee. Confidential and proprietary.

import { useEffect, useMemo, useState, useCallback, type KeyboardEvent, type ReactElement } from 'react'
import {
    X as CloseIcon,
    User as UserIcon,
    Trash2 as TrashIcon,
    RefreshCw as RefreshIcon,
    Link as LinkIcon,
    Check as CheckIcon,
    Globe as GlobeIcon,
} from 'lucide-react'
import type { SessionCollaboratorsResponse } from 'models-ts/session'
import {
    getSessionCollaborators,
    inviteSessionCollaborator,
    revokeSessionInvite,
    updateSession,
} from '../../services/session'
import styles from './ShareModal.module.css'
import { useModalDialog } from '../../hooks/useModalDialog'

interface ShareModalProps {
    isOpen: boolean
    onClose: () => void
    sessionId: string
    canManage: boolean
    privacy?: string
    onPrivacyChange?: (privacy: string) => void
    onRemoveUser: (userId: string) => Promise<void>
}

export function ShareModal({
    isOpen,
    onClose,
    sessionId,
    canManage,
    privacy,
    onPrivacyChange,
    onRemoveUser,
}: ShareModalProps): ReactElement | null {
    const [email, setEmail] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [linkCopied, setLinkCopied] = useState(false)
    const [collaborators, setCollaborators] = useState<SessionCollaboratorsResponse | null>(
        null
    )
    const { dialogRef } = useModalDialog<HTMLDivElement>(isOpen, onClose)

    const isPublic = privacy === 'public'
    const shareUrl = `${window.location.origin}/session/${sessionId}`

    const handleCopyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(shareUrl)
            setLinkCopied(true)
            setTimeout(() => setLinkCopied(false), 2000)
        } catch {
            setError('Failed to copy link')
        }
    }, [shareUrl])

    const handleMakePublic = useCallback(async () => {
        if (!canManage) return
        setIsLoading(true)
        try {
            await updateSession(sessionId, { privacy: 'public' })
            onPrivacyChange?.('public')
        } catch {
            setError('Failed to update privacy')
        } finally {
            setIsLoading(false)
        }
    }, [canManage, sessionId, onPrivacyChange])

    async function refreshCollaborators(): Promise<void> {
        setError(null)
        setIsLoading(true)
        try {
            const data = await getSessionCollaborators(sessionId)
            setCollaborators(data)
        } catch (e) {
            console.error('Failed to fetch collaborators', e)
            setError('Failed to fetch collaborators')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!isOpen) return
        void refreshCollaborators()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, sessionId])

    const accepted = collaborators?.accepted ?? []
    const pending = collaborators?.pending ?? []

    const ownerLabel = useMemo(() => {
        const owner = collaborators?.owner
        if (!owner) return 'Owner'
        return owner.display_name ?? owner.email ?? shortId(owner.id)
    }, [collaborators])

    if (!isOpen) return null

    async function handleAdd() {
        const trimmedEmail = email.trim().toLowerCase()
        if (!trimmedEmail || isLoading || !canManage) return

        if (!trimmedEmail.includes('@') || trimmedEmail.length < 3) {
            setError('Please enter a valid email address')
            return
        }

        setError(null)
        setIsLoading(true)
        try {
            await inviteSessionCollaborator(sessionId, trimmedEmail)
            setEmail('')
            await refreshCollaborators()
        } catch (e) {
            console.error('Failed to invite user', e)
            setError('Failed to invite collaborator')
        } finally {
            setIsLoading(false)
        }
    }

    async function handleRemove(id: string) {
        if (isLoading || !canManage) return
        setIsLoading(true)
        try {
            await onRemoveUser(id)
            await refreshCollaborators()
        } catch (e) {
            console.error('Failed to remove user', e)
            setError('Failed to remove collaborator')
        } finally {
            setIsLoading(false)
        }
    }

    async function handleResendInvite(invitedEmail: string) {
        if (isLoading || !canManage) return
        setIsLoading(true)
        try {
            await inviteSessionCollaborator(sessionId, invitedEmail)
            await refreshCollaborators()
        } catch (e) {
            console.error('Failed to resend invite', e)
            setError('Failed to resend invite')
        } finally {
            setIsLoading(false)
        }
    }

    async function handleRevokeInvite(inviteId: string) {
        if (isLoading || !canManage) return
        setIsLoading(true)
        try {
            await revokeSessionInvite(sessionId, inviteId)
            await refreshCollaborators()
        } catch (e) {
            console.error('Failed to revoke invite', e)
            setError('Failed to revoke invite')
        } finally {
            setIsLoading(false)
        }
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            void handleAdd()
        }
    }

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="shareModalTitle"
                tabIndex={-1}
                className={styles.modal}
                onClick={e => e.stopPropagation()}
            >
                <header className={styles.header}>
                    <h2 id="shareModalTitle" className={styles.title}>Share Session</h2>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        <CloseIcon size={20} />
                    </button>
                </header>

                <div className={styles.content}>
                    {/* Public link sharing section */}
                    <div className={styles.linkSection}>
                        {isPublic ? (
                            <>
                                <div className={styles.linkHeader}>
                                    <GlobeIcon size={14} />
                                    <span>Anyone with this link can view</span>
                                </div>
                                <div className={styles.linkRow}>
                                    <input
                                        className={styles.linkInput}
                                        value={shareUrl}
                                        readOnly
                                        onClick={e => (e.target as HTMLInputElement).select()}
                                    />
                                    <button
                                        className={styles.copyButton}
                                        onClick={() => void handleCopyLink()}
                                        data-copied={linkCopied}
                                    >
                                        {linkCopied ? <CheckIcon size={14} /> : <LinkIcon size={14} />}
                                        {linkCopied ? 'Copied' : 'Copy'}
                                    </button>
                                </div>
                            </>
                        ) : canManage ? (
                            <div className={styles.linkRow}>
                                <span className={styles.privateNote}>
                                    This session is private. Make it public to share a link.
                                </span>
                                <button
                                    className={styles.makePublicButton}
                                    onClick={() => void handleMakePublic()}
                                    disabled={isLoading}
                                >
                                    <GlobeIcon size={14} />
                                    Make public
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <p className={styles.description}>
                        {canManage
                            ? 'Invite collaborators by email. Pending invites are accepted automatically after sign-in.'
                            : 'Collaborators on this session.'}
                    </p>

                    {canManage && (
                        <div className={styles.addSection}>
                            <input
                                className={styles.input}
                                type="email"
                                placeholder="Enter email address to invite..."
                                value={email}
                                onChange={e => {
                                    setEmail(e.target.value)
                                    setError(null)
                                }}
                                onKeyDown={handleKeyDown}
                                disabled={isLoading}
                            />
                            <button
                                className={styles.addButton}
                                onClick={() => void handleAdd()}
                                disabled={!email.trim() || isLoading}
                            >
                                {isLoading ? 'Inviting...' : 'Invite'}
                            </button>
                        </div>
                    )}

                    {error && <div className={styles.error}>{error}</div>}

                    <div className={styles.list}>
                        <div className={styles.item}>
                            <div className={styles.userInfo}>
                                <UserIcon className={styles.userIcon} />
                                <span className={styles.userLabel}>{ownerLabel}</span>
                            </div>
                            <span className={styles.ownerBadge}>Owner</span>
                        </div>

                        {accepted.map(u => (
                            <div key={u.id} className={styles.item}>
                                <div className={styles.userInfo}>
                                    <UserIcon className={styles.userIcon} />
                                    <span className={styles.userLabel}>
                                        {u.display_name ?? u.email ?? shortId(u.id)}
                                    </span>
                                </div>
                                {canManage && (
                                    <button
                                        className={styles.removeButton}
                                        onClick={() => void handleRemove(u.id)}
                                        disabled={isLoading}
                                        title="Remove user"
                                    >
                                        <TrashIcon size={16} />
                                    </button>
                                )}
                            </div>
                        ))}

                        {pending.map(inv => (
                            <div key={inv.invite_id} className={styles.item}>
                                <div className={styles.userInfo}>
                                    <UserIcon className={styles.userIcon} />
                                    <span className={styles.userLabel}>{inv.email}</span>
                                </div>
                                <div className={styles.actions}>
                                    <span className={styles.pendingBadge}>Pending</span>
                                    {canManage && (
                                        <>
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => void handleResendInvite(inv.email)}
                                                disabled={isLoading}
                                                title="Resend invite"
                                            >
                                                <RefreshIcon size={16} />
                                            </button>
                                            <button
                                                className={styles.removeButton}
                                                onClick={() => void handleRevokeInvite(inv.invite_id)}
                                                disabled={isLoading}
                                                title="Revoke invite"
                                            >
                                                <TrashIcon size={16} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}

                        {accepted.length === 0 && pending.length === 0 && (
                            <div className={styles.emptyList}>No collaborators yet</div>
                        )}

                        <div className={styles.footerRow}>
                            <button
                                className={styles.refreshButton}
                                onClick={() => void refreshCollaborators()}
                                disabled={isLoading}
                            >
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function shortId(id: string): string {
    if (!id) return ''
    return id.length <= 8 ? id : id.slice(0, 8)
}

